import type { CustomRenderMethodInput, Map } from "maplibre-gl";
import type { Feature, FeatureCollection, MultiPolygon, Point, Polygon, Position } from "geojson";
import { collectBuildingShadowGeometry } from "./shadow-heightmap";
import { computeSunUniforms } from "./sun";
import type {
  BenchPointCollection,
  CafePointCollection,
  ShadowConfig,
  ShadowTimelineEntry,
  SunUniforms,
  TileHeightMesh,
} from "./types";

type ShadowResources = {
  heightProgram: WebGLProgram;
  shadowProgram: WebGLProgram;
  heightVertexBuffer: WebGLBuffer;
  quadVertexBuffer: WebGLBuffer;
  heightTexture: WebGLTexture;
  framebuffer: WebGLFramebuffer;
};
type GL = WebGLRenderingContext | WebGL2RenderingContext;
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const EARTH_RADIUS_M = 6_378_137;
const EXIT_EPSILON_PX = 2;

const HEIGHT_VERTEX_SHADER = `
attribute vec2 a_pos;
attribute float a_height;
uniform vec2 u_resolution;
uniform vec2 u_paddingPx;
uniform float u_maxHeight;
varying float v_heightNorm;

void main() {
  vec2 zeroToOne = (a_pos + u_paddingPx) / u_resolution;
  vec2 clip = vec2(zeroToOne.x * 2.0 - 1.0, 1.0 - zeroToOne.y * 2.0);
  gl_Position = vec4(clip, 0.0, 1.0);
  v_heightNorm = clamp(a_height / u_maxHeight, 0.0, 1.0);
}
`;

const HEIGHT_FRAGMENT_SHADER = `
precision mediump float;
varying float v_heightNorm;

void main() {
  gl_FragColor = vec4(v_heightNorm, 0.0, 0.0, 1.0);
}
`;

const SHADOW_VERTEX_SHADER = `
attribute vec2 a_pos;
varying vec2 v_uv;

void main() {
  v_uv = (a_pos + 1.0) * 0.5;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

const SHADOW_FRAGMENT_SHADER = `
precision mediump float;
varying vec2 v_uv;
uniform sampler2D u_heightTex;
uniform vec2 u_resolution;
uniform vec2 u_viewResolution;
uniform vec2 u_paddingPx;
uniform vec2 u_sunDir;
uniform float u_tanAltitude;
uniform float u_maxHeight;
uniform float u_shadowOpacity;
uniform float u_nightOpacity;
uniform float u_pixelsPerMeter;
uniform float u_stepSizePx;
uniform int u_steps;

void main() {
  if (u_tanAltitude <= 0.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, u_nightOpacity);
    return;
  }

  vec2 pixel = u_paddingPx + (v_uv * u_viewResolution);
  float alpha = 0.0;

  for (int i = 0; i < 48; i++) {
    if (i >= u_steps) {
      break;
    }

    float distancePx = float(i + 1) * u_stepSizePx;
    vec2 samplePixel = pixel + (u_sunDir * distancePx);

    if (samplePixel.x < 0.0 || samplePixel.y < 0.0 || samplePixel.x > u_resolution.x || samplePixel.y > u_resolution.y) {
      continue;
    }

    vec2 sampleUv = samplePixel / u_resolution;
    float hNorm = texture2D(u_heightTex, sampleUv).r;
    float hMeters = hNorm * u_maxHeight;
    float distanceMeters = distancePx / max(u_pixelsPerMeter, 0.0001);
    float requiredHeight = distanceMeters * u_tanAltitude;

    if (hMeters > requiredHeight) {
      alpha = u_shadowOpacity;
      break;
    }
  }

  gl_FragColor = vec4(0.0, 0.0, 0.0, alpha);
}
`;

function createShader(gl: GL, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) {
    throw new Error("Failed to create shader");
  }

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? "unknown shader compile error";
    gl.deleteShader(shader);
    throw new Error(log);
  }

  return shader;
}

function createProgram(gl: GL, vertexSrc: string, fragmentSrc: string): WebGLProgram {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexSrc);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentSrc);
  const program = gl.createProgram();

  if (!program) {
    throw new Error("Failed to create program");
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);

  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const log = gl.getProgramInfoLog(program) ?? "unknown link error";
    gl.deleteProgram(program);
    throw new Error(log);
  }

  return program;
}

function createTextureAndFramebuffer(
  gl: GL,
  width: number,
  height: number,
): { texture: WebGLTexture; framebuffer: WebGLFramebuffer } {
  const texture = gl.createTexture();
  const framebuffer = gl.createFramebuffer();

  if (!texture || !framebuffer) {
    throw new Error("Failed to allocate FBO resources");
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    throw new Error(`Framebuffer incomplete: ${status}`);
  }

  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.bindTexture(gl.TEXTURE_2D, null);

  return { texture, framebuffer };
}

function destinationFromBearing(
  latDeg: number,
  lonDeg: number,
  bearingRad: number,
  distanceMeters: number,
): [number, number] {
  const latRad = latDeg * DEG_TO_RAD;
  const dNorth = Math.cos(bearingRad) * distanceMeters;
  const dEast = Math.sin(bearingRad) * distanceMeters;

  const dLat = dNorth / EARTH_RADIUS_M;
  const cosLat = Math.max(0.1, Math.cos(latRad));
  const dLon = dEast / (EARTH_RADIUS_M * cosLat);

  const outLat = latDeg + dLat * RAD_TO_DEG;
  const outLon = lonDeg + dLon * RAD_TO_DEG;
  return [outLon, outLat];
}

type Point2D = { x: number; y: number };
type BuildingLayerRef = { source: string; sourceLayer: string };

function collectBuildingLayerRefs(map: Map): BuildingLayerRef[] {
  const layers = map.getStyle().layers ?? [];
  const refs = new Set<string>();
  const result: BuildingLayerRef[] = [];

  for (const layer of layers) {
    if (layer.type !== "fill-extrusion") {
      continue;
    }
    if (!layer.source || !("source-layer" in layer) || !layer["source-layer"]) {
      continue;
    }

    const key = `${layer.source}|${layer["source-layer"]}`;
    if (refs.has(key)) {
      continue;
    }
    refs.add(key);
    result.push({ source: layer.source, sourceLayer: layer["source-layer"] });
  }

  return result;
}

function projectRing(map: Map, ring: Position[]): Point2D[] {
  const points: Point2D[] = [];
  for (const coord of ring) {
    const lng = coord[0];
    const lat = coord[1];
    if (typeof lng !== "number" || typeof lat !== "number") {
      continue;
    }
    const p = map.project([lng, lat]);
    points.push({ x: p.x, y: p.y });
  }
  return points;
}

function pointInRing(point: Point2D, ring: Point2D[]): boolean {
  if (ring.length < 3) {
    return false;
  }
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]?.x ?? 0;
    const yi = ring[i]?.y ?? 0;
    const xj = ring[j]?.x ?? 0;
    const yj = ring[j]?.y ?? 0;

    const intersects = yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || 1e-9) + xi;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInPolygonRings(point: Point2D, rings: Point2D[][]): boolean {
  const outer = rings[0];
  if (!outer || !pointInRing(point, outer)) {
    return false;
  }

  for (let i = 1; i < rings.length; i += 1) {
    const hole = rings[i];
    if (hole && pointInRing(point, hole)) {
      return false;
    }
  }

  return true;
}

function closestPointOnSegment(p: Point2D, a: Point2D, b: Point2D): Point2D {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const apx = p.x - a.x;
  const apy = p.y - a.y;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq <= 1e-9) {
    return a;
  }
  const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLenSq));
  return { x: a.x + abx * t, y: a.y + aby * t };
}

function closestBoundaryPoint(point: Point2D, rings: Point2D[][]): { point: Point2D; distSq: number } | null {
  let best: { point: Point2D; distSq: number } | null = null;

  for (const ring of rings) {
    if (ring.length < 2) {
      continue;
    }

    for (let i = 0; i < ring.length; i += 1) {
      const a = ring[i];
      const b = ring[(i + 1) % ring.length];
      if (!a || !b) {
        continue;
      }
      const candidate = closestPointOnSegment(point, a, b);
      const dx = candidate.x - point.x;
      const dy = candidate.y - point.y;
      const distSq = dx * dx + dy * dy;
      if (!best || distSq < best.distSq) {
        best = { point: candidate, distSq };
      }
    }
  }

  return best;
}

export class GroundShadowSystem {
  private map: Map;
  private gl: GL;
  private config: ShadowConfig;
  private resources: ShadowResources | null = null;
  private sunUniforms: SunUniforms | null = null;
  private meshData: Float32Array = new Float32Array();
  private meshVertexCount = 0;
  private tileMeshes: TileHeightMesh[] = [];
  private geometryDirty = true;
  private viewportWidth = 0;
  private viewportHeight = 0;
  private visible = true;
  private fboPixelsPerMeter = 1;
  private readonly shadowPaddingPx: number;

  constructor(map: Map, gl: GL, config: ShadowConfig) {
    this.map = map;
    this.gl = gl;
    this.config = config;
    this.shadowPaddingPx = Math.ceil(config.maxShadowLengthPx + 8);
  }

  init(): void {
    const gl = this.gl;

    const heightProgram = createProgram(gl, HEIGHT_VERTEX_SHADER, HEIGHT_FRAGMENT_SHADER);
    const shadowProgram = createProgram(gl, SHADOW_VERTEX_SHADER, SHADOW_FRAGMENT_SHADER);

    const heightVertexBuffer = gl.createBuffer();
    const quadVertexBuffer = gl.createBuffer();

    if (!heightVertexBuffer || !quadVertexBuffer) {
      throw new Error("Failed to create shadow buffers");
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, quadVertexBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([
        -1, -1,
        1, -1,
        -1, 1,
        -1, 1,
        1, -1,
        1, 1,
      ]),
      gl.STATIC_DRAW,
    );

    const canvas = this.map.getCanvas();
    const viewWidth = Math.max(1, Math.floor(canvas.width * this.config.textureScale));
    const viewHeight = Math.max(1, Math.floor(canvas.height * this.config.textureScale));
    const width = viewWidth + this.shadowPaddingPx * 2;
    const height = viewHeight + this.shadowPaddingPx * 2;
    const { texture, framebuffer } = createTextureAndFramebuffer(gl, width, height);

    this.viewportWidth = width;
    this.viewportHeight = height;

    this.resources = {
      heightProgram,
      shadowProgram,
      heightVertexBuffer,
      quadVertexBuffer,
      heightTexture: texture,
      framebuffer,
    };
  }

  dispose(): void {
    const gl = this.gl;
    if (!this.resources) {
      return;
    }

    gl.deleteProgram(this.resources.heightProgram);
    gl.deleteProgram(this.resources.shadowProgram);
    gl.deleteBuffer(this.resources.heightVertexBuffer);
    gl.deleteBuffer(this.resources.quadVertexBuffer);
    gl.deleteTexture(this.resources.heightTexture);
    gl.deleteFramebuffer(this.resources.framebuffer);

    this.resources = null;
  }

  setSunTime(date: Date): void {
    this.sunUniforms = computeSunUniforms(date, this.map);
    this.fboPixelsPerMeter = this.computeFboPixelsPerMeter();
  }

  invalidateGeometry(): void {
    this.geometryDirty = true;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
  }

  private resizeTargets(force = false): void {
    if (!this.resources) {
      return;
    }

    const canvas = this.map.getCanvas();
    const viewWidth = Math.max(1, Math.floor(canvas.width * this.config.textureScale));
    const viewHeight = Math.max(1, Math.floor(canvas.height * this.config.textureScale));
    const width = viewWidth + this.shadowPaddingPx * 2;
    const height = viewHeight + this.shadowPaddingPx * 2;

    if (!force && width === this.viewportWidth && height === this.viewportHeight) {
      return;
    }

    this.viewportWidth = width;
    this.viewportHeight = height;

    if (this.resources) {
      this.gl.deleteTexture(this.resources.heightTexture);
      this.gl.deleteFramebuffer(this.resources.framebuffer);
    }

    const { texture, framebuffer } = createTextureAndFramebuffer(this.gl, width, height);
    this.resources.heightTexture = texture;
    this.resources.framebuffer = framebuffer;

    this.geometryDirty = true;
    this.fboPixelsPerMeter = this.computeFboPixelsPerMeter();
  }

  private computeFboPixelsPerMeter(): number {
    const canvas = this.map.getCanvas();
    const center = this.map.getCenter();
    const referenceMeters = 50;
    const scaleX = (canvas.width / Math.max(1, canvas.clientWidth)) * this.config.textureScale;
    const scaleY = (canvas.height / Math.max(1, canvas.clientHeight)) * this.config.textureScale;

    const bearingRad = (this.sunUniforms?.azimuthRad ?? 0) + Math.PI;
    const source = this.map.project([center.lng, center.lat]);
    const [targetLng, targetLat] = destinationFromBearing(center.lat, center.lng, bearingRad, referenceMeters);
    const target = this.map.project([targetLng, targetLat]);

    const dx = (target.x - source.x) * scaleX;
    const dy = (target.y - source.y) * scaleY;
    const distanceFboPx = Math.hypot(dx, dy);

    if (!Number.isFinite(distanceFboPx) || distanceFboPx <= 0.0001) {
      return 1;
    }

    return distanceFboPx / referenceMeters;
  }

  private rebuildGeometryIfNeeded(): void {
    if (!this.geometryDirty) {
      return;
    }

    const geometry = collectBuildingShadowGeometry(this.map, this.config);
    this.meshData = geometry.vertexData;
    this.meshVertexCount = geometry.vertexCount;
    this.tileMeshes = geometry.tileMeshes;

    if (this.resources && this.meshVertexCount > 0) {
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.resources.heightVertexBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, this.meshData, this.gl.DYNAMIC_DRAW);
    }

    this.geometryDirty = false;
  }

  private renderHeightTexturePass(): void {
    if (!this.resources || this.meshVertexCount === 0) {
      return;
    }

    const gl = this.gl;
    const { heightProgram, heightVertexBuffer, framebuffer } = this.resources;

    gl.disable(gl.BLEND);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.viewport(0, 0, this.viewportWidth, this.viewportHeight);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.useProgram(heightProgram);

    const heightPos = gl.getAttribLocation(heightProgram, "a_pos");
    const heightValue = gl.getAttribLocation(heightProgram, "a_height");
    const resolutionUniform = gl.getUniformLocation(heightProgram, "u_resolution");
    const paddingUniform = gl.getUniformLocation(heightProgram, "u_paddingPx");
    const maxHeightUniform = gl.getUniformLocation(heightProgram, "u_maxHeight");

    gl.bindBuffer(gl.ARRAY_BUFFER, heightVertexBuffer);
    gl.enableVertexAttribArray(heightPos);
    gl.vertexAttribPointer(heightPos, 2, gl.FLOAT, false, 12, 0);
    gl.enableVertexAttribArray(heightValue);
    gl.vertexAttribPointer(heightValue, 1, gl.FLOAT, false, 12, 8);

    gl.uniform2f(resolutionUniform, this.viewportWidth, this.viewportHeight);
    gl.uniform2f(paddingUniform, this.shadowPaddingPx, this.shadowPaddingPx);
    gl.uniform1f(maxHeightUniform, this.config.maxBuildingHeightMeters);
    gl.drawArrays(gl.TRIANGLES, 0, this.meshVertexCount);
    gl.disableVertexAttribArray(heightPos);
    gl.disableVertexAttribArray(heightValue);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  private classifyPointShadows<T extends { inShadow?: boolean }>(
    points: FeatureCollection<Point, T>,
  ): FeatureCollection<Point, T> {
    if (!this.resources || !this.sunUniforms) {
      return points;
    }

    if (this.sunUniforms.tanAltitude <= 0) {
      return {
        type: "FeatureCollection",
        features: points.features.map(feature => ({
          ...feature,
          properties: {
            ...feature.properties,
            inShadow: true,
          },
        })),
      };
    }

    this.resizeTargets();
    this.rebuildGeometryIfNeeded();
    this.renderHeightTexturePass();

    const gl = this.gl;
    const { framebuffer } = this.resources;
    const pixelBuffer = new Uint8Array(this.viewportWidth * this.viewportHeight * 4);

    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.readPixels(0, 0, this.viewportWidth, this.viewportHeight, gl.RGBA, gl.UNSIGNED_BYTE, pixelBuffer);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    const canvas = this.map.getCanvas();
    const scaleX = (canvas.width / Math.max(1, canvas.clientWidth)) * this.config.textureScale;
    const scaleY = (canvas.height / Math.max(1, canvas.clientHeight)) * this.config.textureScale;
    const paddingPx = this.shadowPaddingPx;
    const stepSizePx = 6;
    const maxSteps = Math.max(1, Math.min(48, Math.floor(this.config.maxShadowLengthPx / stepSizePx)));
    let sunDir = this.sunUniforms.directionScreen;
    sunDir = [sunDir[0], sunDir[1] * -1]; // invert the direction to fix the shadow direction
    const tanAltitude = this.sunUniforms.tanAltitude;

    const buildingPolygons: Point2D[][][] = [];
    for (const layerRef of collectBuildingLayerRefs(this.map)) {
      const features = this.map.querySourceFeatures(layerRef.source, {
        sourceLayer: layerRef.sourceLayer,
        validate: false,
      });

      for (const feature of features) {
        const geometry = feature.geometry;
        if (!geometry) {
          continue;
        }

        if (geometry.type === "Polygon") {
          const rings = (geometry as Polygon).coordinates.map(ring => projectRing(this.map, ring));
          if (rings.length > 0) {
            buildingPolygons.push(rings);
          }
        } else if (geometry.type === "MultiPolygon") {
          for (const polygon of (geometry as MultiPolygon).coordinates) {
            const rings = polygon.map(ring => projectRing(this.map, ring));
            if (rings.length > 0) {
              buildingPolygons.push(rings);
            }
          }
        }
      }
    }

    const sampleHeightMeters = (x: number, y: number): number => {
      if (x < 0 || y < 0 || x >= this.viewportWidth || y >= this.viewportHeight) {
        return 0;
      }
      const imageY = this.viewportHeight - 1 - Math.floor(y);
      const imageX = Math.floor(x);
      const offset = (imageY * this.viewportWidth + imageX) * 4;
      const heightNorm = (pixelBuffer[offset] ?? 0) / 255;
      return heightNorm * this.config.maxBuildingHeightMeters;
    };

    const moveToClosestExitOutside = (point: Point2D): Point2D | null => {
      let best: { boundary: Point2D; distSq: number } | null = null;

      for (const polygonRings of buildingPolygons) {
        if (!pointInPolygonRings(point, polygonRings)) {
          continue;
        }

        const boundary = closestBoundaryPoint(point, polygonRings);
        if (!boundary) {
          continue;
        }

        if (!best || boundary.distSq < best.distSq) {
          best = { boundary: boundary.point, distSq: boundary.distSq };
        }
      }

      if (!best) {
        return null;
      }

      let vx = best.boundary.x - point.x;
      let vy = best.boundary.y - point.y;
      let len = Math.hypot(vx, vy);

      if (len < 1e-6) {
        vx = sunDir[0];
        vy = sunDir[1];
        len = Math.hypot(vx, vy);
      }
      if (len < 1e-6) {
        vx = 1;
        vy = 0;
        len = 1;
      }

      const ux = vx / len;
      const uy = vy / len;
      return {
        x: best.boundary.x + ux * EXIT_EPSILON_PX,
        y: best.boundary.y + uy * EXIT_EPSILON_PX,
      };
    };

    const isShadowed = (feature: Feature<Point, T>): boolean => {
      const coordinates = feature.geometry.coordinates;
      const lng = coordinates[0];
      const lat = coordinates[1];
      if (typeof lng !== "number" || typeof lat !== "number") {
        return false;
      }
      const projected = this.map.project([lng, lat]);
      const projectedPoint: Point2D = { x: projected.x, y: projected.y };
      const outsidePoint = moveToClosestExitOutside(projectedPoint) ?? projectedPoint;

      let baseX = outsidePoint.x * scaleX + paddingPx;
      let baseY = outsidePoint.y * scaleY + paddingPx;

      if (baseX < 0 || baseY < 0 || baseX >= this.viewportWidth || baseY >= this.viewportHeight) {
        return false;
      }

      for (let i = 0; i < maxSteps; i += 1) {
        const distancePx = (i + 1) * stepSizePx;
        const sampleX = baseX + sunDir[0] * distancePx;
        const sampleY = baseY + sunDir[1] * distancePx;

        if (sampleX < 0 || sampleY < 0 || sampleX >= this.viewportWidth || sampleY >= this.viewportHeight) {
          continue;
        }

        const heightMeters = sampleHeightMeters(sampleX, sampleY);
        const distanceMeters = distancePx / Math.max(this.fboPixelsPerMeter, 0.0001);
        const requiredHeight = distanceMeters * tanAltitude;

        if (heightMeters > requiredHeight) {
          return true;
        }
      }

      return false;
    };

    const classified: FeatureCollection<Point, T> = {
      type: "FeatureCollection",
      features: points.features.map(feature => ({
        ...feature,
        properties: {
          ...feature.properties,
          inShadow: isShadowed(feature),
        },
      })),
    };
    return classified;
  }

  computeShadowTimeline(
    coordinates: [number, number],
    date: Date,
    sunriseMinutes: number,
    sunsetMinutes: number,
    intervalMinutes = 15,
  ): ShadowTimelineEntry[] {
    if (!this.resources) {
      return [];
    }

    this.resizeTargets();
    this.rebuildGeometryIfNeeded();
    this.renderHeightTexturePass();

    const gl = this.gl;
    const { framebuffer } = this.resources;
    const pixelBuffer = new Uint8Array(this.viewportWidth * this.viewportHeight * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.readPixels(0, 0, this.viewportWidth, this.viewportHeight, gl.RGBA, gl.UNSIGNED_BYTE, pixelBuffer);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    const canvas = this.map.getCanvas();
    const scaleX = (canvas.width / Math.max(1, canvas.clientWidth)) * this.config.textureScale;
    const scaleY = (canvas.height / Math.max(1, canvas.clientHeight)) * this.config.textureScale;
    const paddingPx = this.shadowPaddingPx;
    const stepSizePx = 6;
    const maxSteps = Math.max(1, Math.min(48, Math.floor(this.config.maxShadowLengthPx / stepSizePx)));

    const buildingPolygons: Point2D[][][] = [];
    for (const layerRef of collectBuildingLayerRefs(this.map)) {
      const features = this.map.querySourceFeatures(layerRef.source, {
        sourceLayer: layerRef.sourceLayer,
        validate: false,
      });
      for (const feature of features) {
        const geometry = feature.geometry;
        if (!geometry) {
          continue;
        }
        if (geometry.type === "Polygon") {
          const rings = (geometry as Polygon).coordinates.map(ring => projectRing(this.map, ring));
          if (rings.length > 0) {
            buildingPolygons.push(rings);
          }
        } else if (geometry.type === "MultiPolygon") {
          for (const polygon of (geometry as MultiPolygon).coordinates) {
            const rings = polygon.map(ring => projectRing(this.map, ring));
            if (rings.length > 0) {
              buildingPolygons.push(rings);
            }
          }
        }
      }
    }

    const [lng, lat] = coordinates;
    const projected = this.map.project([lng, lat]);
    const projectedPoint: Point2D = { x: projected.x, y: projected.y };

    let outsidePoint = projectedPoint;
    for (const polygonRings of buildingPolygons) {
      if (!pointInPolygonRings(projectedPoint, polygonRings)) {
        continue;
      }
      const boundary = closestBoundaryPoint(projectedPoint, polygonRings);
      if (!boundary) {
        continue;
      }
      let vx = boundary.point.x - projectedPoint.x;
      let vy = boundary.point.y - projectedPoint.y;
      let len = Math.hypot(vx, vy);
      if (len < 1e-6) {
        vx = 1;
        vy = 0;
        len = 1;
      }
      outsidePoint = {
        x: boundary.point.x + (vx / len) * EXIT_EPSILON_PX,
        y: boundary.point.y + (vy / len) * EXIT_EPSILON_PX,
      };
      break;
    }

    const baseX = outsidePoint.x * scaleX + paddingPx;
    const baseY = outsidePoint.y * scaleY + paddingPx;

    if (baseX < 0 || baseY < 0 || baseX >= this.viewportWidth || baseY >= this.viewportHeight) {
      return [];
    }

    const sampleHeightMeters = (x: number, y: number): number => {
      if (x < 0 || y < 0 || x >= this.viewportWidth || y >= this.viewportHeight) {
        return 0;
      }
      const imageY = this.viewportHeight - 1 - Math.floor(y);
      const imageX = Math.floor(x);
      const offset = (imageY * this.viewportWidth + imageX) * 4;
      const heightNorm = (pixelBuffer[offset] ?? 0) / 255;
      return heightNorm * this.config.maxBuildingHeightMeters;
    };

    const center = this.map.getCenter();
    const referenceMeters = 50;
    const computePixelsPerMeter = (azimuthRad: number): number => {
      const bearingRad = azimuthRad + Math.PI;
      const source = this.map.project([center.lng, center.lat]);
      const [targetLng, targetLat] = destinationFromBearing(center.lat, center.lng, bearingRad, referenceMeters);
      const target = this.map.project([targetLng, targetLat]);
      const dx = (target.x - source.x) * scaleX;
      const dy = (target.y - source.y) * scaleY;
      const distanceFboPx = Math.hypot(dx, dy);
      if (!Number.isFinite(distanceFboPx) || distanceFboPx <= 0.0001) {
        return 1;
      }
      return distanceFboPx / referenceMeters;
    };

    const results: ShadowTimelineEntry[] = [];
    const dayDate = new Date(date);
    dayDate.setHours(0, 0, 0, 0);

    for (let minutes = sunriseMinutes; minutes <= sunsetMinutes; minutes += intervalMinutes) {
      const testDate = new Date(dayDate);
      testDate.setMinutes(minutes);
      const sunUniforms = computeSunUniforms(testDate, this.map);

      if (sunUniforms.tanAltitude <= 0) {
        results.push({ minutesOfDay: minutes, inShadow: true });
        continue;
      }

      const pixelsPerMeter = computePixelsPerMeter(sunUniforms.azimuthRad);
      const sunDir: [number, number] = [sunUniforms.directionScreen[0], sunUniforms.directionScreen[1] * -1];

      let shadowed = false;
      for (let i = 0; i < maxSteps; i += 1) {
        const distancePx = (i + 1) * stepSizePx;
        const sampleX = baseX + sunDir[0] * distancePx;
        const sampleY = baseY + sunDir[1] * distancePx;

        if (sampleX < 0 || sampleY < 0 || sampleX >= this.viewportWidth || sampleY >= this.viewportHeight) {
          continue;
        }

        const heightMeters = sampleHeightMeters(sampleX, sampleY);
        const distanceMeters = distancePx / Math.max(pixelsPerMeter, 0.0001);
        const requiredHeight = distanceMeters * sunUniforms.tanAltitude;

        if (heightMeters > requiredHeight) {
          shadowed = true;
          break;
        }
      }

      results.push({ minutesOfDay: minutes, inShadow: shadowed });
    }

    return results;
  }

  classifyCafeShadows(cafes: CafePointCollection): CafePointCollection {
    return this.classifyPointShadows(cafes);
  }

  classifyBenchShadows(benches: BenchPointCollection): BenchPointCollection {
    return this.classifyPointShadows(benches);
  }

  render(_input: CustomRenderMethodInput): void {
    if (!this.resources || !this.visible) {
      return;
    }

    this.resizeTargets();
    this.rebuildGeometryIfNeeded();

    if (!this.sunUniforms || this.meshVertexCount === 0) {
      return;
    }

    this.renderHeightTexturePass();

    const gl = this.gl;
    const { shadowProgram, quadVertexBuffer, heightTexture } = this.resources;

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    gl.viewport(0, 0, this.map.getCanvas().width, this.map.getCanvas().height);

    gl.useProgram(shadowProgram);

    const quadPos = gl.getAttribLocation(shadowProgram, "a_pos");
    const texUniform = gl.getUniformLocation(shadowProgram, "u_heightTex");
    const shadowResolutionUniform = gl.getUniformLocation(shadowProgram, "u_resolution");
    const viewResolutionUniform = gl.getUniformLocation(shadowProgram, "u_viewResolution");
    const paddingUniform = gl.getUniformLocation(shadowProgram, "u_paddingPx");
    const sunDirUniform = gl.getUniformLocation(shadowProgram, "u_sunDir");
    const tanAltUniform = gl.getUniformLocation(shadowProgram, "u_tanAltitude");
    const maxHeightShadowUniform = gl.getUniformLocation(shadowProgram, "u_maxHeight");
    const opacityUniform = gl.getUniformLocation(shadowProgram, "u_shadowOpacity");
    const nightOpacityUniform = gl.getUniformLocation(shadowProgram, "u_nightOpacity");
    const pixelsPerMeterUniform = gl.getUniformLocation(shadowProgram, "u_pixelsPerMeter");
    const stepSizeUniform = gl.getUniformLocation(shadowProgram, "u_stepSizePx");
    const stepsUniform = gl.getUniformLocation(shadowProgram, "u_steps");

    gl.bindBuffer(gl.ARRAY_BUFFER, quadVertexBuffer);
    gl.enableVertexAttribArray(quadPos);
    gl.vertexAttribPointer(quadPos, 2, gl.FLOAT, false, 0, 0);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, heightTexture);

    const stepSizePx = 6;
    const maxSteps = Math.max(1, Math.min(48, Math.floor(this.config.maxShadowLengthPx / stepSizePx)));
    const viewWidth = Math.max(1, Math.floor(this.map.getCanvas().width * this.config.textureScale));
    const viewHeight = Math.max(1, Math.floor(this.map.getCanvas().height * this.config.textureScale));

    gl.uniform1i(texUniform, 0);
    gl.uniform2f(shadowResolutionUniform, this.viewportWidth, this.viewportHeight);
    gl.uniform2f(viewResolutionUniform, viewWidth, viewHeight);
    gl.uniform2f(paddingUniform, this.shadowPaddingPx, this.shadowPaddingPx);
    gl.uniform2f(sunDirUniform, this.sunUniforms.directionScreen[0], this.sunUniforms.directionScreen[1]);
    gl.uniform1f(tanAltUniform, this.sunUniforms.tanAltitude);
    gl.uniform1f(maxHeightShadowUniform, this.config.maxBuildingHeightMeters);
    gl.uniform1f(opacityUniform, this.config.opacityCap);
    gl.uniform1f(nightOpacityUniform, this.config.nightOpacity);
    gl.uniform1f(pixelsPerMeterUniform, this.fboPixelsPerMeter);
    gl.uniform1f(stepSizeUniform, stepSizePx);
    gl.uniform1i(stepsUniform, maxSteps);

    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.disableVertexAttribArray(quadPos);
  }

  getDiagnostics(): { meshCount: number; vertexCount: number; sunReady: boolean } {
    return {
      meshCount: this.tileMeshes.length,
      vertexCount: this.meshVertexCount,
      sunReady: this.sunUniforms !== null,
    };
  }
}

export const DEFAULT_SHADOW_CONFIG: ShadowConfig = {
  opacityCap: 0.25,
  nightOpacity: 0.25,
  defaultBuildingHeightMeters: 9,
  maxBuildingHeightMeters: 400,
  maxShadowLengthPx: 300,
  textureScale: 0.5,
};
